import {Heading, VStack, Text, useMultiStyleConfig, StackProps, TextProps, Box, Flex, BoxProps, FlexProps, Link} from '@chakra-ui/react'
import {CtaButton, Picture, ScrollDownButton, Timer, Video, Input, Icon, LiveText} from '_uiComponents'
import React, {JSX, useEffect, useRef, useState} from 'react'
import {useAnalytics, useIsElementInViewport, usePageDesigner, useScrollAtTop, useViewport, useOpenDrawer} from '_customHooks'
import {createRedirectUrl, updateCloudinaryPDImage, updateCloudinaryPDVideo, updateCloudinaryPDPoster, PAGE_DESIGNER_MODULES, searchUrlBuilder} from '_utils'
import {LinkProps, useHistory} from 'react-router-dom'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {getProductAnalyticsInfo, getProductImpressionAnalytics, toEditorialAnalyticsList} from '_analytics/utils/analyticsUtils'
import {CLDImageAsset, CLDVideoAsset} from '_types'
import {useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

interface HeroBannerProps {
    imgDesk?: CLDImageAsset
    imgTitle?: string
    imgTablet?: CLDImageAsset
    imgMobile?: CLDImageAsset
    imgDeskRatio?: string
    imgTabletRatio?: string
    imgMobileRatio?: string
    padding?: boolean
    imgFullHeight?: boolean
    videoDesk?: CLDVideoAsset
    videoMobile?: CLDVideoAsset
    ctaText?: string
    ctaLink?: string
    ctaAccesibility?: string
    ctaText2?: string
    ctaLink2?: string
    ctaAccesibility2?: string
    verticalAlign?: 'top' | 'center' | 'bottom'
    horizontalAlign?: 'left' | 'center' | 'right'
    classes?: string
    extraCSS?: string
    eyelet?: string
    title: string
    text?: string
    ctaInlineDesk?: boolean
    ctaInlineMobile?: boolean
    ctaStyle?: 'text' | 'button'
    colorContent?: string
    colorCta?: string
    bgCta?: string
    contentTextAlign?: 'left' | 'center' | 'right'
    isShowScroll?: boolean
    scrollArrowColor?: string
    countdownTimer?: string
    isImgZoom?: boolean
    animationTime?: string
    isAnimationTitle?: boolean
    isAnimationCta?: boolean
    analyticsID?: string
    variant?: string
    position: number
    isMainBanner?: boolean
    listingLayout: string
    analyticsList?: string
    textBottomMobile?: boolean
    analyticsName?: string
    isBannerHalf?: boolean
    adaptiveHeight?: boolean
    editorialSearch?: boolean
    editorialSearchHint?: string
    /** WCAG 1.4.5 – Live text equivalent for burned-in text in the image */
    accessibilityImageLivetext?: string
}

const defaultColorContent = 'color.text.white'

/**
 * HeroBanner component renders a styled banner displaying an image or video with over a text and a link.
 *
 * @param {CLDImageAsset} imgDesk - The cloudinary object of the image for desktop.
 * @param {CLDImageAsset} imgTablet - The cloudinary object of the image for tablet.
 * @param {CLDImageAsset} imgMobile - The cloudinary object of the image for mobile.
 * @param {string} [imgTitle = ''] - The alt for the image.
 * @param {boolean} [padding = false] - The possibility to have a padding all around.
 * @param {boolean} [imgFullHeight = false] - The possibility to have the image to take up the all viewport height.
 * @param {CLDVideoAsset} videoDesk - The URL of the video for desktop screen.
 * @param {CLDVideoAsset} videoMobile - The URL of the video for mobile screen.
 * @param {string} ctaText - The label for the first cta link
 * @param {string} ctaLink - The URL for the first cta
 * @param {string} ctaAccesibility - The accessibility label for the first cta
 * @param {string} ctaText2 - The label for the second cta link
 * @param {string} ctaLink2 - The URL for the second cta
 * @param {string} ctaAccesibility2 - The accessibility label for the second cta
 * @param {string} [verticalAlign = 'center'] - The vertical alignment of the content
 * @param {string} [horizontalAlign = 'center'] - The horizontal alignment of the content
 * @param {string} [contentTextAlign = 'center'] - The text alignment of the content
 * @param {string} classes - The extra classes for the container
 * @param {string} extraCSS - The extra CSS for the container
 * @param {string} [eyelet = ''] - The eyelet text
 * @param {string} title - The title of the body
 * @param {string} text - The description of the body
 * @param {boolean} [ctaInlineDesk = false ] - The possibility to have cta element on same line on desktop screens
 * @param {boolean} [ctaInlineMobile = false ] - The possibility to have cta element on same line on mobile screens, when ctaStyle = text
 * @param {string} [colorContent = 'color.text.white' ] - The possibility color of the content
 * @param {string} colorCta - The color of the cta text and border
 * @param {string} bgCta- The background color for a cta style in button
 * @param {boolean} [isShowScroll = false] - the possibility to have an arrow to scroll down
 * @param {boolean} [scrollArrowColor = 'color.icon.white'] The color of the arrow button to scroll down
 * @param {string} countdownTimer - The end date of the countdown
 * @param {boolean} [isImgZoom = false] The possibility to have a zoom of the image when first landing happens
 * @param {boolean} isAnimationCta - The possibility of animation of underline for a text cta
 * @param {boolean} [isAnimationTitle = false] - The possibility of animation for title form right to left when loading
 * @param {string} [animationTime = '1000'] - The time in ms of the animations duration
 * @param {string} ctaStyle - The type of cta
 * @param {string} props.variant - The variant to be used for styling the link.
 * @param {string} props.analyticsID - The analytics id to send for the tracking
 * @param {number} props.position - The position of the element with respect to other page designer elements in the page
 * @param {string} [props.analyticsList] - PLP Category analyticsList.
 * @param {string} [props.analyticsName] - Page Designer analyticsName, depending from included page type
 * @param {boolean} [props.editorialSearch] - Whether or not the module has a search bar
 * @param {string} [props.editorialSearchHint] - The text hint that should appear in the search bar
 * @param {React.ReactNode} props.children - The content to be displayed inside the link.
 *
 * @returns {JSX.Element} The rendered HeroBanner component.
 */
const HeroBanner = ({
    imgDesk,
    imgTablet,
    imgMobile,
    imgTitle = '',
    padding = false,
    imgFullHeight = false,
    ctaText,
    ctaLink,
    ctaAccesibility,
    verticalAlign = 'center',
    horizontalAlign = 'center',
    contentTextAlign = 'center',
    videoDesk,
    videoMobile,
    ctaText2,
    ctaLink2,
    ctaAccesibility2,
    classes,
    extraCSS,
    eyelet,
    title,
    text,
    ctaInlineDesk = false,
    ctaInlineMobile = false,
    colorContent: _colorContent = defaultColorContent,
    colorCta,
    bgCta,
    isShowScroll = false,
    scrollArrowColor = 'color.icon.white',
    countdownTimer,
    isImgZoom = false,
    animationTime = '1000',
    isAnimationTitle = false,
    isAnimationCta,
    ctaStyle,
    variant: _variant,
    analyticsID,
    position,
    imgDeskRatio,
    imgMobileRatio,
    imgTabletRatio,
    isMainBanner,
    listingLayout,
    analyticsList,
    textBottomMobile,
    analyticsName,
    isBannerHalf = false,
    adaptiveHeight = false,
    editorialSearch = false,
    editorialSearchHint = '',
    accessibilityImageLivetext,
}: HeroBannerProps): JSX.Element => {
    const {isMobile} = useViewport()

    const isTextBottomMobile = textBottomMobile && isMobile
    const variant = _variant ? _variant : isTextBottomMobile ? 'textBottomMobile' : undefined
    const colorContent = isTextBottomMobile && _colorContent === defaultColorContent ? 'color.text.black' : _colorContent

    const styles = useMultiStyleConfig('HeroBanner', {
        padding,
        colorContent,
        contentTextAlign,
        verticalAlign,
        horizontalAlign,
        scrollArrowColor,
        isImgZoom,
        animationTime,
        isAnimationTitle,
        colorCta,
        bgCta,
        ctaInlineDesk,
        ctaInlineMobile: ctaInlineMobile && ctaStyle == 'text',
        imgFullHeight,
        imgDeskRatio,
        imgMobileRatio,
        imgTabletRatio,
        variant,
        adaptiveHeight: adaptiveHeight || isBannerHalf,
        editorialSearch,
    }) as Record<string, StackProps & FlexProps & TextProps & BoxProps & LinkProps>

    const isAtTop = useScrollAtTop()
    const {handleShopTheImageClick} = useOpenDrawer()
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const {linkObject, globalImageTransformation, globalVideoTransformation, CloudinaryPageDesignerCNAME} = usePageDesigner()
    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const history = useHistory()

    const resizedImgMobile = imgMobile ? updateCloudinaryPDImage(imgMobile, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const resizedImgTablet = imgTablet ? updateCloudinaryPDImage(imgTablet, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const resizedImgDesk = imgDesk ? updateCloudinaryPDImage(imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''
    const resizedImgXlDesk = imgDesk ? updateCloudinaryPDImage(imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME) : ''

    const [alreadySentPromoView, setAlreadySentPromoView] = useState(false)
    const [alreadySentProductImpression, setAlreadySentProductImpression] = useState(false)
    const {analyticsEventPromoView, analyticsEventPromoClick, analyticsProductClick, analyticsProductImpression, analyticsEventInternalSearch} = useAnalytics()
    const [productID, setProductID] = useState<string | undefined>()
    const [editorialSearchQueryError, setEditorialSearchQueryError] = useState(false)
    const editorialSearchInputRef = useRef<HTMLInputElement | null>(null)

    const playerDeskConf = videoDesk && JSON.parse(videoDesk.playerConf)
    const playerMobileConf = videoMobile && JSON.parse(videoMobile.playerConf)
    const isControlDesk = playerDeskConf && playerDeskConf.playerConfig?.controls
    const isAutoplayDesk = playerDeskConf && playerDeskConf.playerConfig?.autoplay
    const isLoopDesk = playerDeskConf && playerDeskConf.playerConfig?.loop
    const isMuteDesk = playerDeskConf && playerDeskConf.playerConfig?.muted
    const isControlMobile = playerMobileConf && playerMobileConf.playerConfig?.controls
    const isAutoplayMobile = playerMobileConf && playerMobileConf.playerConfig?.autoplay
    const isLoopMobile = playerMobileConf && playerMobileConf.playerConfig?.loop
    const isMuteMobile = playerMobileConf && playerMobileConf.playerConfig?.muted
    const hasSingleCta = ctaLink && !(ctaText2 && ctaLink2)
    const hasVideoControls = !imgDesk && ((isMobile && (playerMobileConf ? isControlMobile : isControlDesk)) || (!isMobile && isControlDesk))
    const isModuleClickable = hasSingleCta && !hasVideoControls && !editorialSearch
    const analyticsListEditorial = toEditorialAnalyticsList(analyticsList)
    const editorialSearchDefaultHint = formatMessage({id: 'label.header.searchwatermark', defaultMessage: 'What are you looking for?'})
    const editorialSearchCharactersError = formatMessage({
        id: 'error.search.charcontrol',
        defaultMessage: 'Error: no special characters are allowed in the search field. Please try again.',
    })
    const forbiddenSearchCharacters = /[<>&%()|!$^`?]/

    const getCtaColor = (): string => {
        const defaultColor = 'color.text.white'

        if (isMobile && textBottomMobile) {
            return ctaStyle === 'text' ? 'color.text.black' : defaultColor
        }

        return colorCta || defaultColor
    }

    const getCtaBackground = (): string => {
        const defaultBg = 'color.background.alpha.light.30'

        if (isMobile && textBottomMobile) {
            return ctaStyle === 'text' ? defaultBg : 'color.text.black'
        }

        return bgCta || defaultBg
    }

    const getCtaBorderColor = (): string => {
        if (isMobile && textBottomMobile) {
            return ctaStyle === 'text' ? '' : 'color.text.black'
        } else {
            return ctaStyle === 'text' ? '' : 'color.text.white'
        }
    }

    const {data: product} = useProduct(
        {
            parameters: {
                id: productID!,
            },
        },
        {
            enabled: !!productID,
        },
    )

    const getAnalyticsCreative = (): string => {
        return `${imgDesk ? PAGE_DESIGNER_MODULES.HERO_XL_FULL : PAGE_DESIGNER_MODULES.HERO_XL_VIDEO_CLOUDINARY}${isBannerHalf ? '-half' : ''}${editorialSearch ? '-search' : ''}`
    }

    const triggerAnalyticsEventPromoClick = (textBtn: string, positionBtn?: string): void => {
        analyticsEventPromoClick({
            id: analyticsID ? analyticsID : 'null',
            position: positionBtn ? String(position) + '-' + positionBtn : String(position),
            creative: getAnalyticsCreative(),
            name: analyticsName ? analyticsName : 'null',
            promoText: textBtn,
        })
    }

    const triggerAnalyticsEventProductClick = (): void => {
        if (product) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsListEditorial, idx: position - 1})

            analyticsProductClick({
                currencyCode: productCurrency,
                dimension25: listingLayout,
                actionField: {
                    list: analyticsListEditorial,
                },
                product: analyticsProductObject,
            })
        }
    }

    const editorialSearchQuery = (): void => {
        const query = editorialSearchInputRef.current?.value

        if (!query) return

        if (forbiddenSearchCharacters.test(query)) {
            setEditorialSearchQueryError(true)
        } else {
            triggerAnalyticsEventPromoClick('')
            analyticsEventInternalSearch({action: 'Editorial Banner', label: query})
            const searchUrl = searchUrlBuilder(query, locale.alias)
            history.push(searchUrl)
        }
    }

    useEffect(() => {
        let productID: string | undefined = undefined
        if (classes && classes.indexOf('js-PD-pid-') > -1) {
            const classList = classes.split('js-PD-pid-')
            if (classList && classList.length > 0) {
                productID = classList[0].trim()
            }
        }
        if (!productID && ctaLink) {
            const matchProduct = ctaLink.match(/[0-9]{3}[a-zA-Z0-9]+/)
            if (matchProduct) productID = matchProduct[0]
        }
        setProductID(productID)
    }, [])

    useEffect(() => {
        if (isVisible && !alreadySentPromoView) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position: position,
                creative: getAnalyticsCreative(),
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySentPromoView(true)
        }
    }, [isVisible])

    useEffect(() => {
        if (isVisible && !alreadySentProductImpression && product) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsListEditorial, idx: position - 1})

            analyticsProductImpression({
                currencyCode: productCurrency,
                template: listingLayout,
                impressions: analyticsProductObject,
            })

            setAlreadySentProductImpression(true)
        }
    }, [isVisible, product])

    return (
        <Box {...styles.wrapper} data-testid="hero-banner" css={extraCSS} ref={targetElement}>
            <Box className={`${classes} cc-pd cc-pd-hero-xl-${imgDesk ? 'full' : 'video'}`}>
                <Flex {...styles.container} className="cc-object-container">
                    {isModuleClickable && (
                        <Link
                            data-testid="hero-banner-full-link"
                            {...styles.link}
                            className="cc-link-full"
                            href={createRedirectUrl(ctaLink, linkObject.hostlink, linkObject.locale, linkObject.site)}
                            title={ctaAccesibility}
                            aria-label={ctaAccesibility || title || 'Hero banner link'}
                            onClick={(e) => {
                                if (handleShopTheImageClick(e, ctaLink)) return
                                if (productID && product) triggerAnalyticsEventProductClick()
                                triggerAnalyticsEventPromoClick('')
                            }}
                        ></Link>
                    )}
                    {imgDesk ? (
                        <Box sx={styles.image} className="cc-content-image">
                            <Picture
                                overflow="hidden"
                                objectFit="cover"
                                imagexs={resizedImgMobile}
                                imagesm={resizedImgTablet || resizedImgDesk}
                                imagelg={resizedImgDesk}
                                imagexl={resizedImgXlDesk}
                                alt={imgTitle}
                                height={{base: '100%', md: 'fit-content'}}
                                zIndex={0}
                                position="absolute"
                                hasLazy={!isMainBanner}
                            />
                        </Box>
                    ) : (
                        <Box sx={styles.video} className={`cc-content-video cc-content-video-${isMobile ? 'mobile' : 'desktop'}`}>
                            {isMobile ? (
                                <Video
                                    overflow="hidden"
                                    height="100%"
                                    position="absolute"
                                    isVideoModule={true}
                                    autoplay={playerMobileConf ? isAutoplayMobile : isAutoplayDesk}
                                    controls={playerMobileConf ? isControlMobile : isControlDesk}
                                    muted={playerMobileConf ? isMuteMobile : isMuteDesk}
                                    loop={playerMobileConf ? isLoopMobile : isLoopDesk}
                                    videoxs={videoMobile ? updateCloudinaryPDVideo(videoMobile, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                                    videolg={videoDesk ? updateCloudinaryPDVideo(videoDesk, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                                    poster={
                                        (videoMobile ? updateCloudinaryPDPoster(videoMobile, globalVideoTransformation, CloudinaryPageDesignerCNAME) : '') ||
                                        (videoDesk ? updateCloudinaryPDPoster(videoDesk, globalVideoTransformation, CloudinaryPageDesignerCNAME) : '')
                                    }
                                    zIndex={1}
                                    objectFit="cover"
                                ></Video>
                            ) : (
                                <Video
                                    overflow="hidden"
                                    height="100%"
                                    position="absolute"
                                    isVideoModule={true}
                                    autoplay={isAutoplayDesk}
                                    controls={isControlDesk}
                                    muted={isMuteDesk}
                                    loop={isLoopDesk}
                                    videolg={videoDesk ? updateCloudinaryPDVideo(videoDesk, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                                    poster={videoDesk ? updateCloudinaryPDPoster(videoDesk, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                                    zIndex={1}
                                    objectFit="cover"
                                ></Video>
                            )}
                        </Box>
                    )}
                    {(title || text || (ctaLink && ctaText) || countdownTimer || (ctaText2 && ctaLink2) || editorialSearch) && (
                        <Flex {...styles.bodyWrapper} className="cc-content-text">
                            <VStack {...styles.body} className="cc-content-elements">
                                {eyelet && (
                                    <Text {...styles.eyelet} className="cc-eyelet">
                                        {eyelet}
                                    </Text>
                                )}
                                {title && (
                                    <Text as="h2" {...styles.title} className="cc-title">
                                        {title}
                                    </Text>
                                )}
                                {text && (
                                    <Text {...styles.text} className="cc-description">
                                        {text}
                                    </Text>
                                )}

                                {editorialSearch && (
                                    <Box {...styles.editorialSearchWrapper}>
                                        <Input
                                            {...styles.editorialSearchInput}
                                            ref={editorialSearchInputRef}
                                            data-testid="editorial-search-input"
                                            name="editorial-search"
                                            type="text"
                                            variant={'flushed'}
                                            placeholder={editorialSearchHint || editorialSearchDefaultHint}
                                            leftIcon={<Icon name="IconSearch" size="7" />}
                                            _placeholder={{color: 'white', fontWeight: 'light'}}
                                            borderColor={'white !important'}
                                            focusBorderColor={'white'}
                                            autoComplete={'off'}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    editorialSearchQuery()
                                                } else {
                                                    if (editorialSearchQueryError) {
                                                        setEditorialSearchQueryError(false)
                                                    }
                                                }
                                            }}
                                            onLeftIconClick={editorialSearchQuery}
                                        ></Input>
                                        {editorialSearchQueryError && (
                                            <Text data-testid="editorial-search-error" {...styles.editorialSearchError}>
                                                {editorialSearchCharactersError}
                                            </Text>
                                        )}
                                    </Box>
                                )}
                                {countdownTimer && new Date() < new Date(countdownTimer) && (
                                    <Box {...styles.timer} className="cc-content-countdown">
                                        <Timer time={countdownTimer} className="cc-countdown" />
                                    </Box>
                                )}
                                {((ctaText2 && ctaLink2) || (ctaText && ctaLink)) && (
                                    <Flex sx={styles.ctaContainer} className="cc-content-buttons" data-testid="hero-banner-ctas">
                                        {ctaText && ctaLink && (
                                            <CtaButton
                                                variant={ctaStyle}
                                                href={ctaLink}
                                                size="sm"
                                                ctaColor={getCtaColor()}
                                                background={getCtaBackground()}
                                                borderColor={getCtaBorderColor()}
                                                isAnimated={isAnimationCta}
                                                animationTime={animationTime}
                                                title={ctaAccesibility}
                                                onClick={(e) => {
                                                    if (handleShopTheImageClick(e, ctaLink)) return
                                                    if (productID && product) triggerAnalyticsEventProductClick()
                                                    triggerAnalyticsEventPromoClick(ctaText, ctaText2 && ctaLink2 ? '1' : '')
                                                }}
                                            >
                                                {ctaText}
                                            </CtaButton>
                                        )}
                                        {ctaText2 && ctaLink2 && (
                                            <CtaButton
                                                variant={ctaStyle}
                                                href={ctaLink2}
                                                size="sm"
                                                ctaColor={getCtaColor()}
                                                isAnimated={isAnimationCta}
                                                animationTime={animationTime}
                                                background={getCtaBackground()}
                                                borderColor={getCtaBorderColor()}
                                                title={ctaAccesibility2}
                                                onClick={(e) => {
                                                    if (handleShopTheImageClick(e, ctaLink2)) return
                                                    triggerAnalyticsEventPromoClick(ctaText2, ctaText && ctaLink ? '2' : '')
                                                }}
                                            >
                                                {ctaText2}
                                            </CtaButton>
                                        )}
                                    </Flex>
                                )}
                            </VStack>
                        </Flex>
                    )}

                    {isShowScroll && isAtTop && (
                        <ScrollDownButton
                            parentDataTestId="hero-banner"
                            className="heroBanner__scroll__btn"
                            dataTestId="hero-banner-scroll-down"
                            ariaLabel="Button scroll down hero banner"
                        />
                    )}
                </Flex>
            </Box>
            {accessibilityImageLivetext && <LiveText>{accessibilityImageLivetext}</LiveText>}
        </Box>
    )
}

export {HeroBanner}
